import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ComposedChart, Bar, BarChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  stockTransfersApi, TransferFilters, TransferGroup, TransferMeta, TransferProductsResponse, TransferShow,
  TransferSummary, TransferTimeline,
} from '../services/stockTransfersApi';
import { PHYSICAL_STORES, fmtEur, fmtInt, fmtLocalDateTime, fmtPct, storeLabel } from '../utils/replenishment';

// Presets de período (dias); 0 = todo o histórico da tabela
const PRESETS: Array<[number, string]> = [[90, '90 dias'], [182, '6 meses'], [365, '12 meses'], [0, 'Tudo']];
const DEFAULT_PRESET = 365;
const PAGE_SIZE = 50;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const WEEKDAYS = ['', 'Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const SHOW_LABELS: Record<TransferShow, string> = {
  ciclos: 'Com ciclos inúteis',
  sem_venda: 'Com voltas sem venda',
  pares: 'Todos com ida→volta',
};

const ORDERING_LABELS: Record<string, string> = {
  ciclos_desc: 'Mais ciclos inúteis',
  sem_venda_desc: 'Mais voltas sem venda',
  valor_ciclos_desc: 'Maior valor em ciclos',
  valor_sem_venda_desc: 'Maior valor sem venda',
  unidades_desc: 'Mais unidades sem venda',
  pct_sem_venda_desc: '% sem venda',
  dias_ida_volta_asc: 'Volta mais rápida',
  ultima_volta_desc: 'Volta mais recente',
  sku_asc: 'SKU',
};

const TIPO_BADGE: Record<string, string> = {
  IDA: 'bg-primary', VOLTA: 'bg-warning text-dark', ENTRE_LOJAS: 'bg-info text-dark', OUTRO: 'bg-secondary',
};

const lisbonToday = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Lisbon' });

const shiftDate = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const fmtMonth = (mes: string): string => `${mes.slice(5, 7)}/${mes.slice(2, 4)}`;
const fmtDays = (v: number | null | undefined): string => (v === null || v === undefined ? '-' : `${v} d`);
// ts vem em hora de Lisboa sem fuso (DATETIME do SAP)
const fmtTs = (ts: string | null): string => (ts ? `${ts.slice(8, 10)}/${ts.slice(5, 7)}/${ts.slice(0, 4)} ${ts.slice(11, 16)}` : '-');

const Kpi: React.FC<{ title: string; value: string; hint?: string; tone?: string }> = ({ title, value, hint, tone }) => (
  <div className="col-6 col-lg">
    <div className={`card h-100 ${tone ? `border-${tone}` : ''}`}>
      <div className="card-body">
        <div className="text-muted small">{title}</div>
        <div className={`fs-3 fw-semibold ${tone ? `text-${tone}` : ''}`}>{value}</div>
        {hint && <div className="text-muted small">{hint}</div>}
      </div>
    </div>
  </div>
);

const TimelineModal: React.FC<{ sku: string; loja?: string; onClose: () => void }> = ({ sku, loja, onClose }) => {
  const [data, setData] = useState<TransferTimeline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [store, setStore] = useState<string | undefined>(loja);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    stockTransfersApi.getTimeline(sku, store)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar a linha temporal'));
    return () => { alive = false; };
  }, [sku, store]);

  return (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,.4)' }} onClick={onClose}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {sku} {data?.product?.nome && <small className="text-muted">— {data.product.nome}</small>}
            </h5>
            <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="d-flex flex-wrap gap-1 mb-3">
              <button className={`btn btn-sm ${!store ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setStore(undefined)}>
                Todas
              </button>
              {PHYSICAL_STORES.map((s) => (
                <button key={s} className={`btn btn-sm ${store === s ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setStore(s)}>
                  {storeLabel(s)}
                </button>
              ))}
            </div>
            {error && <div className="alert alert-warning">{error}</div>}
            {!data && !error && <div className="text-muted">A carregar…</div>}
            {data && (
              <>
                {data.by_store.length > 0 && (
                  <table className="table table-sm w-auto mb-3">
                    <thead className="table-light">
                      <tr><th>Loja</th><th className="text-end">Idas</th><th className="text-end">Voltas</th>
                        <th className="text-end">Voltas sem venda</th><th className="text-end">Ciclos inúteis</th></tr>
                    </thead>
                    <tbody>
                      {data.by_store.map((s) => (
                        <tr key={s.loja}>
                          <td>{storeLabel(s.loja)}</td><td className="text-end">{s.idas}</td><td className="text-end">{s.voltas}</td>
                          <td className="text-end">{s.sem_venda}</td><td className="text-end fw-semibold">{s.ciclos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {data.truncated && <div className="alert alert-info py-1 small">Mostradas só as primeiras 1000 transferências.</div>}
                <div className="table-responsive">
                  <table className="table table-sm table-hover small">
                    <thead className="table-light">
                      <tr>
                        <th>Quando</th><th>Tipo</th><th>Fluxo</th><th className="text-end">Un.</th><th className="text-end">Valor</th>
                        <th>Entrega</th><th>Utilizador</th><th className="text-end">Dias desde a ida</th>
                        <th className="text-end">Vendidas entre</th><th className="text-end">Reenvio após</th><th>Diagnóstico</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.events.map((e) => (
                        <tr key={`${e.entrega}-${e.origem}-${e.destino}`} className={e.ciclo_inutil ? 'table-danger' : e.sem_venda ? 'table-warning' : ''}>
                          <td className="text-nowrap">{fmtTs(e.ts)}</td>
                          <td><span className={`badge ${TIPO_BADGE[e.tipo]}`}>{e.tipo.replace('_', ' ')}</span></td>
                          <td className="text-nowrap">{e.origem} → {e.destino}</td>
                          <td className="text-end">{e.qty}</td>
                          <td className="text-end">{fmtEur(e.valor)}</td>
                          <td>{e.entrega}</td>
                          <td>{e.utilizador || '-'}</td>
                          <td className="text-end">{fmtDays(e.dias_ida_volta)}</td>
                          <td className="text-end">{e.vendidas_entre ?? '-'}</td>
                          <td className="text-end">{fmtDays(e.dias_volta_reenvio)}</td>
                          <td>
                            {e.ciclo_inutil ? 'Ciclo inútil' : e.sem_venda ? 'Volta sem venda'
                              : e.vendidas_entre ? 'Volta com venda' : e.tipo === 'VOLTA' && e.prev_tipo !== 'IDA' ? 'Volta (sem ida antes)' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StockTransfersPage: React.FC = () => {
  const today = useMemo(lisbonToday, []);
  const [params, setParams] = useSearchParams();

  // Estado partilhável no URL
  const preset = params.has('preset') ? Number(params.get('preset')) : DEFAULT_PRESET;
  const start = ISO_DATE.test(params.get('start') || '') ? params.get('start')!
    : preset > 0 ? shiftDate(today, -(preset - 1)) : '';
  const end = ISO_DATE.test(params.get('end') || '') ? params.get('end')! : preset > 0 ? today : '';
  const stores = useMemo(
    () => (params.get('stores') || '').split(',').filter((c) => PHYSICAL_STORES.includes(c)),
    [params]);
  const cat1 = params.get('cat1') || '';
  const search = params.get('q') || '';
  const group = (params.get('group') === 'sku_loja' ? 'sku_loja' : 'sku') as TransferGroup;
  const show = (['ciclos', 'sem_venda', 'pares'].includes(params.get('show') || '') ? params.get('show') : 'ciclos') as TransferShow;
  const ordering = ORDERING_LABELS[params.get('ordering') || ''] ? params.get('ordering')! : 'ciclos_desc';
  const page = Math.max(0, Number(params.get('page')) || 0);
  const skuParam = params.get('sku');
  const skuLoja = params.get('sku_loja') || undefined;

  const patchParams = useCallback((changes: Record<string, string>, push = false) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: !push });
  }, [setParams]);

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);

  const [meta, setMeta] = useState<TransferMeta | null>(null);
  const [summary, setSummary] = useState<TransferSummary | null>(null);
  const [products, setProducts] = useState<TransferProductsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters: TransferFilters = useMemo(() => ({
    start: start || undefined, end: end || undefined, stores, cat1: cat1 || undefined, search: search || undefined,
  }), [start, end, stores, cat1, search]);

  useEffect(() => {
    stockTransfersApi.getMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    stockTransfersApi.getSummary(filters)
      .then((d) => alive && setSummary(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar o resumo'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filters]);

  const productsQuery = useMemo(() => ({
    ...filters, group, show, ordering, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
  }), [filters, group, show, ordering, page]);

  useEffect(() => {
    let alive = true;
    setLoadingProducts(true);
    stockTransfersApi.getProducts(productsQuery)
      .then((d) => alive && setProducts(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar os produtos'))
      .finally(() => alive && setLoadingProducts(false));
    return () => { alive = false; };
  }, [productsQuery]);

  const toggleStore = (s: string) => {
    const next = stores.includes(s) ? stores.filter((x) => x !== s) : [...stores, s].sort();
    patchParams({ stores: next.length === PHYSICAL_STORES.length ? '' : next.join(','), page: '' });
  };

  const t = summary?.totals;
  const cycleDays = summary?.cycle_days ?? meta?.cycle_days ?? 30;
  const totalPages = products ? Math.ceil(products.count / PAGE_SIZE) : 0;

  return (
    <div className="pb-5">
      <div className="d-flex flex-wrap justify-content-between align-items-end mb-2 gap-2">
        <div>
          <h2 className="mb-0">🔄 Transferências armazém ↔ lojas</h2>
          <div className="text-muted small">
            IDA = T000 → loja · VOLTA = loja → T000 · <strong>volta sem venda</strong> = voltou sem nenhuma venda desse produto
            na loja desde a ida · <strong>ciclo inútil</strong> = volta sem venda seguida de nova ida para a mesma loja em ≤ {cycleDays} dias.
            Contado na data da volta.
          </div>
        </div>
        <div className="text-muted small text-end">
          {meta?.min_date && <>Dados de {meta.min_date.split('-').reverse().join('/')} a {meta.max_date?.split('-').reverse().join('/')}<br /></>}
          {meta?.calculated_at && <>Actualizado: {fmtLocalDateTime(meta.calculated_at)}</>}
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div>
            <div className="small text-muted">Período</div>
            <div className="btn-group btn-group-sm">
              {PRESETS.map(([days, label]) => (
                <button key={days} className={`btn ${!params.get('start') && preset === days ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => patchParams({ preset: days === DEFAULT_PRESET ? '' : String(days), start: '', end: '', page: '' })}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="st-start">De</label>
            <input id="st-start" type="date" className="form-control form-control-sm" value={start}
              onChange={(e) => patchParams({ start: e.target.value, end: end || today, page: '' })} />
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="st-end">Até</label>
            <input id="st-end" type="date" className="form-control form-control-sm" value={end}
              onChange={(e) => patchParams({ end: e.target.value, start: start || shiftDate(e.target.value, -364), page: '' })} />
          </div>
          <div>
            <div className="small text-muted">Lojas {stores.length === 0 && '(todas)'}</div>
            <div className="btn-group btn-group-sm flex-wrap">
              {PHYSICAL_STORES.map((s) => (
                <button key={s} className={`btn ${stores.includes(s) ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => toggleStore(s)}>
                  {storeLabel(s)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="st-cat1">Categoria</label>
            <select id="st-cat1" className="form-select form-select-sm" value={cat1}
              onChange={(e) => patchParams({ cat1: e.target.value, page: '' })}>
              <option value="">Todas</option>
              {(meta?.cat1 || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <form className="d-flex gap-1" onSubmit={(e) => { e.preventDefault(); patchParams({ q: searchInput.trim(), page: '' }); }}>
            <div>
              <label className="small text-muted d-block" htmlFor="st-q">SKU ou nome</label>
              <input id="st-q" className="form-control form-control-sm" value={searchInput} placeholder="P078478, teclado…"
                onChange={(e) => setSearchInput(e.target.value)} />
            </div>
            <button className="btn btn-sm btn-outline-secondary align-self-end" type="submit">Filtrar</button>
          </form>
          {loading && <div className="spinner-border spinner-border-sm text-primary align-self-center" role="status" />}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* KPIs */}
      {t && (
        <div className="row g-2 mb-3">
          <Kpi title="Idas (T000 → loja)" value={fmtInt(t.un_idas)} hint={`${fmtInt(t.idas)} transferências · ${fmtEur(t.valor_idas)}`} />
          <Kpi title="Voltas (loja → T000)" value={fmtInt(t.un_voltas)} hint={`${fmtInt(t.voltas)} transf. · ${fmtPct(t.pct_voltas)} das idas`} />
          <Kpi title="Voltas sem venda" value={fmtPct(t.pct_sem_venda)} tone="warning"
            hint={`${fmtInt(t.sem_venda)} de ${fmtInt(t.pares)} voltas após ida · ${fmtInt(t.un_sem_venda)} un. · ${fmtEur(t.valor_sem_venda)}`} />
          <Kpi title="Ciclos inúteis" value={fmtInt(t.ciclos)} tone="danger"
            hint={`${fmtInt(t.un_ciclos)} un. · ${fmtEur(t.valor_ciclos)} · ${fmtInt(t.produtos_ciclos || 0)} produtos`} />
          <Kpi title="Medianas" value={`${fmtDays(t.med_dias_ida_volta)} / ${fmtDays(t.med_dias_reenvio)}`}
            hint="ida → volta sem venda / volta → reenvio" />
        </div>
      )}

      {summary && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <div className="card h-100">
                <div className="card-body">
                  <h6>Evolução mensal</h6>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={summary.by_month.map((m) => ({ ...m, mesLabel: fmtMonth(m.mes) }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mesLabel" />
                      <YAxis yAxisId="n" />
                      <YAxis yAxisId="pct" orientation="right" unit="%" domain={[0, 100]} />
                      <Tooltip formatter={(v: any, name: any) => (String(name).startsWith('%') ? fmtPct(Number(v)) : fmtInt(Number(v)))} />
                      <Legend />
                      <Bar yAxisId="n" dataKey="sem_venda" name="Voltas sem venda" fill="#f0ad4e" />
                      <Bar yAxisId="n" dataKey="ciclos" name="Ciclos inúteis" fill="#d9534f" />
                      <Line yAxisId="pct" dataKey="pct_sem_venda" name="% sem venda" stroke="#212529" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="small text-muted">Os ciclos dos últimos {cycleDays} dias ainda podem crescer (o reenvio pode não ter acontecido).</div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card h-100">
                <div className="card-body">
                  <h6>Dias entre a ida e a volta sem venda</h6>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={summary.dias_ida_volta_hist}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" />
                      <YAxis />
                      <Tooltip formatter={(v: any) => fmtInt(Number(v))} />
                      <Bar dataKey="n" name="Voltas sem venda" fill="#f0ad4e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <h6>Por loja <small className="text-muted">(clicar para filtrar)</small></h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Loja</th><th className="text-end">Un. idas</th><th className="text-end">Un. voltas</th><th className="text-end">% voltas</th>
                      <th className="text-end">Voltas após ida</th><th className="text-end">Sem venda</th><th className="text-end">% sem venda</th>
                      <th className="text-end">Ciclos inúteis</th><th className="text-end">% ciclos</th><th className="text-end">Valor sem venda</th>
                      <th className="text-end">Valor ciclos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_store.map((s) => (
                      <tr key={s.loja} role="button" className={stores.length === 1 && stores[0] === s.loja ? 'table-active' : ''}
                        onClick={() => patchParams({ stores: stores.length === 1 && stores[0] === s.loja ? '' : s.loja, page: '' })}>
                        <td>{storeLabel(s.loja)}</td>
                        <td className="text-end">{fmtInt(s.un_idas)}</td><td className="text-end">{fmtInt(s.un_voltas)}</td>
                        <td className="text-end">{fmtPct(s.pct_voltas)}</td><td className="text-end">{fmtInt(s.pares)}</td>
                        <td className="text-end">{fmtInt(s.sem_venda)}</td><td className="text-end">{fmtPct(s.pct_sem_venda)}</td>
                        <td className="text-end fw-semibold">{fmtInt(s.ciclos)}</td><td className="text-end">{fmtPct(s.pct_ciclos)}</td>
                        <td className="text-end">{fmtEur(s.valor_sem_venda)}</td><td className="text-end">{fmtEur(s.valor_ciclos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-body">
                  <h6>Por categoria</h6>
                  <table className="table table-sm mb-0">
                    <thead className="table-light">
                      <tr><th>Categoria</th><th className="text-end">Voltas após ida</th><th className="text-end">Sem venda</th>
                        <th className="text-end">Ciclos</th><th className="text-end">Produtos</th><th className="text-end">Valor ciclos</th></tr>
                    </thead>
                    <tbody>
                      {summary.by_category.map((c) => (
                        <tr key={c.cat1} role="button" onClick={() => c.cat1 !== '(sem categoria)' && patchParams({ cat1: cat1 === c.cat1 ? '' : c.cat1, page: '' })}>
                          <td>{c.cat1}</td><td className="text-end">{fmtInt(c.pares)}</td><td className="text-end">{fmtInt(c.sem_venda)}</td>
                          <td className="text-end fw-semibold">{fmtInt(c.ciclos)}</td><td className="text-end">{fmtInt(c.produtos)}</td>
                          <td className="text-end">{fmtEur(c.valor_ciclos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card mb-3">
                <div className="card-body">
                  <h6>Voltas por dia da semana</h6>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={summary.by_weekday.map((d) => ({ ...d, dia: WEEKDAYS[d.dow] }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dia" />
                      <YAxis />
                      <Tooltip formatter={(v: any) => fmtInt(Number(v))} />
                      <Legend />
                      <Bar dataKey="voltas" name="Voltas" fill="#adb5bd" />
                      <Bar dataKey="sem_venda" name="Sem venda" fill="#f0ad4e" />
                      <Bar dataKey="ciclos" name="Ciclos" fill="#d9534f" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="card">
                <div className="card-body">
                  <h6>Quem lança as voltas <small className="text-muted">(CREATEDBYUSER, top 15)</small></h6>
                  <table className="table table-sm mb-0 small">
                    <thead className="table-light">
                      <tr><th>Utilizador</th><th>Lojas</th><th className="text-end">Voltas</th><th className="text-end">Sem venda</th><th className="text-end">Ciclos</th></tr>
                    </thead>
                    <tbody>
                      {summary.by_user.map((u) => (
                        <tr key={u.utilizador}>
                          <td>{u.utilizador}</td><td>{u.lojas.join(', ')}</td><td className="text-end">{fmtInt(u.voltas)}</td>
                          <td className="text-end">{fmtInt(u.sem_venda)}</td><td className="text-end">{fmtInt(u.ciclos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <details className="card mb-3">
            <summary className="card-header">Matriz de fluxos (todas as plants, inclui N000/R000 e entre lojas)</summary>
            <div className="card-body table-responsive">
              <table className="table table-sm mb-0 small">
                <thead className="table-light">
                  <tr><th>Origem</th><th>Destino</th><th>Tipo</th><th className="text-end">Entregas</th><th className="text-end">Produtos</th>
                    <th className="text-end">Unidades</th><th className="text-end">Valor</th></tr>
                </thead>
                <tbody>
                  {summary.flows.map((f) => (
                    <tr key={`${f.origem}-${f.destino}-${f.tipo}`}>
                      <td>{f.origem}</td><td>{f.destino}</td>
                      <td><span className={`badge ${TIPO_BADGE[f.tipo]}`}>{f.tipo.replace('_', ' ')}</span></td>
                      <td className="text-end">{fmtInt(f.entregas)}</td><td className="text-end">{fmtInt(f.produtos)}</td>
                      <td className="text-end">{fmtInt(f.unidades)}</td><td className="text-end">{fmtEur(f.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}

      {/* Produtos */}
      <div className="card">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
            <h6 className="me-auto mb-0">
              Produtos {products && <small className="text-muted">({fmtInt(products.count)})</small>}
              {loadingProducts && <span className="spinner-border spinner-border-sm ms-2" role="status" />}
            </h6>
            <div className="btn-group btn-group-sm">
              <button className={`btn ${group === 'sku' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => patchParams({ group: '', page: '' })}>Por produto</button>
              <button className={`btn ${group === 'sku_loja' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => patchParams({ group: 'sku_loja', page: '' })}>Por produto × loja</button>
            </div>
            <select className="form-select form-select-sm w-auto" aria-label="Mostrar" value={show}
              onChange={(e) => patchParams({ show: e.target.value === 'ciclos' ? '' : e.target.value, page: '' })}>
              {Object.entries(SHOW_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="form-select form-select-sm w-auto" aria-label="Ordenar" value={ordering}
              onChange={(e) => patchParams({ ordering: e.target.value === 'ciclos_desc' ? '' : e.target.value, page: '' })}>
              {Object.entries(ORDERING_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <a className="btn btn-sm btn-outline-success" href={stockTransfersApi.exportUrl(productsQuery)}>⬇ CSV</a>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover small mb-2">
              <thead className="table-light">
                <tr>
                  <th>SKU</th>{group === 'sku_loja' && <th>Loja</th>}<th>Nome</th><th>Categoria</th><th>Estado</th>
                  {group === 'sku' && <th>Lojas</th>}
                  <th className="text-end">Voltas após ida</th><th className="text-end">Sem venda</th><th className="text-end">% sem venda</th>
                  <th className="text-end">Ciclos</th><th className="text-end">Un. sem venda</th><th className="text-end">Valor sem venda</th>
                  <th className="text-end">Valor ciclos</th><th className="text-end">Med. ida→volta</th><th className="text-end">Med. reenvio</th>
                  <th>Última volta</th>
                </tr>
              </thead>
              <tbody>
                {products?.results.map((r) => (
                  <tr key={`${r.sku}-${r.loja || ''}`} role="button"
                    onClick={() => patchParams({ sku: r.sku, sku_loja: r.loja || '' }, true)}>
                    <td className="text-nowrap">{r.sku}</td>
                    {group === 'sku_loja' && <td>{r.loja && storeLabel(r.loja)}</td>}
                    <td>{r.nome}</td>
                    <td>{r.cat2 || r.cat1}</td>
                    <td>{r.estado}</td>
                    {group === 'sku' && <td className="text-nowrap">{r.lojas.join(' ')}</td>}
                    <td className="text-end">{r.pares}</td>
                    <td className="text-end">{r.voltas_sem_venda}</td>
                    <td className="text-end">{fmtPct(r.pct_sem_venda)}</td>
                    <td className="text-end fw-semibold">{r.ciclos_inuteis}</td>
                    <td className="text-end">{r.unidades_sem_venda}</td>
                    <td className="text-end">{fmtEur(r.valor_sem_venda)}</td>
                    <td className="text-end">{fmtEur(r.valor_ciclos)}</td>
                    <td className="text-end">{fmtDays(r.med_dias_ida_volta)}</td>
                    <td className="text-end">{fmtDays(r.med_dias_reenvio)}</td>
                    <td className="text-nowrap">{fmtTs(r.ultima_volta)}</td>
                  </tr>
                ))}
                {products && products.results.length === 0 && (
                  <tr><td colSpan={16} className="text-muted text-center">Sem produtos para estes filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="d-flex gap-2 align-items-center">
              <button className="btn btn-sm btn-outline-secondary" disabled={page === 0}
                onClick={() => patchParams({ page: page - 1 ? String(page - 1) : '' })}>‹ Anterior</button>
              <span className="small">Página {page + 1} de {totalPages}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={page + 1 >= totalPages}
                onClick={() => patchParams({ page: String(page + 1) })}>Seguinte ›</button>
            </div>
          )}
          <div className="small text-muted mt-2">
            Valor = valor de stock (custo) do movimento no SAP. O motivo das voltas não existe nos dados: uma volta sem venda pode ser
            legítima (defeito, montra, encomenda não levantada, rebalanceamento).
          </div>
        </div>
      </div>

      {skuParam && (
        <TimelineModal sku={skuParam} loja={skuLoja} onClose={() => patchParams({ sku: '', sku_loja: '' })} />
      )}
    </div>
  );
};

export default StockTransfersPage;
